/**
 * English source — shared/top-level component strings (`src/components/*.tsx`).
 * Product names (MeshGen, SimOps, Diagnosis) stay untranslated as brand
 * identity. Grouped by component; keys are camelCase.
 */
export default {
  transcriptLink: {
    view: 'View AI transcript',
  },
  pipeline: {
    ariaLabel: 'Product pipeline',
    diagnosisHint: 'Inspect and repair the CAD until it is mesh-ready.',
    meshgenHint: 'Mesh the clean geometry.',
    simopsHint: 'Simulate the mesh — one case or a full study.',
    evaltoolHint:
      'Evaluate results and compare designs — the evaluation workbench inside a completed study.',
  },
  common: {
    beta: 'Beta',
    unknownError: 'Unknown error',
  },
  // The compute gate's 403s at submit time (ENG-3091, `@/lib/entitlementDenial`).
  entitlement: {
    noSeat:
      "You don't have a seat for {{product}} in this workspace. Ask your workspace admin to assign you one, or contact support.",
    noSubscription:
      "This workspace doesn't have an active subscription for {{product}}. Ask your workspace admin to subscribe, or contact support.",
    thisProduct: 'this product',
  },
  // Typed API refusals `apiErrorMessage` says in the reader's language
  // instead of the server's sentence (ENG-3123).
  apiRefusal: {
    artifactTooLarge:
      "This run's results are too large to read here. Download the run to open them in ParaView.",
  },
  fileUpload: {
    tooLarge: 'File is too large ({{size}}). Maximum size is 1 GB.',
    invalidType:
      'Invalid file type. Only STEP, STP, OBJ, and STL files are accepted.',
    rejected: 'File rejected.',
    uploading: 'Uploading {{name}}...',
    uploadingPercent: 'Uploading {{percent}}%',
    uploadingNoName: 'Uploading...',
    openCadFile: 'Open CAD file...',
    dragAndDrop: 'Drag and drop file here',
    limit: 'Limit 1GB per file • STEP, STP, OBJ, STL',
    browseFiles: 'Browse files',
  },
  floatingProgress: {
    processing: 'PROCESSING',
    liveComputing: 'Live Computing',
  },
  meshTimer: {
    running: 'Running',
    complete: 'Complete',
    error: 'Error',
    ready: 'Ready',
  },
  processStatus: {
    noActiveProject: 'No active project',
    status: 'Status',
    fileUploaded: 'File Uploaded',
    generatingMesh: 'Generating Mesh',
    meshComplete: 'Mesh Complete',
    strategy: 'Strategy',
  },
  promoCode: {
    havePromoCode: 'Have a promo code?',
    enterCode: 'Enter code',
    redeem: 'Redeem',
  },
  qualityHistogram: {
    distribution: 'Quality Distribution',
    noData: 'No quality data available',
    clickPrefix: 'Click',
    generateMesh: 'Generate Mesh',
    clickSuffix: 'to create a mesh and view quality metrics.',
    excellent: 'Excellent',
    good: 'Good',
    fair: 'Fair',
    poor: 'Poor',
    bad: 'Bad',
    badToGood: 'Bad → Good',
    estimated: '~Estimated',
    realData: 'Real Data',
    min: 'Min',
    avg: 'Avg',
    max: 'Max',
    elements: '{{count}} elements',
    scaleTitle: 'SICN Quality Scale:',
    scaleBad: '0.0-0.2: Bad',
    scalePoor: '0.2-0.4: Poor',
    scaleFair: '0.4-0.6: Fair',
    scaleGood: '0.6-0.8: Good',
    scaleGreat: '0.8-1.0: Great',
  },
  qualityReport: {
    title: 'Quality Report',
    pass: 'PASS',
    flag: 'FLAG',
    elements: 'Elements',
    poor: 'Poor (<0.1)',
    sicnMin: 'SICN (min)',
    gammaMin: 'Gamma (min)',
    maxSkewness: 'Max Skewness',
    maxAspectRatio: 'Max Aspect Ratio',
    cfdQuality: 'CFD Quality',
    ready: 'Ready',
    issues: 'Issues',
    nonOrthogonality: 'Non-Orthogonality',
    cfdSkewness: 'CFD Skewness',
    facePyramids: 'Face Pyramids',
    ok: 'OK',
    facePyramidsFlag: 'FLAG',
    cfdReadyMesh: 'CFD-ready mesh (non-ortho ≤70°)',
    meetsStandards: 'Mesh quality meets all standards',
  },
  shareLink: {
    copyPublicLink: 'Copy public link',
  },
  visibilityBadge: {
    makePrivate: 'Click to make private',
    makePublic: 'Click to make public',
    public: 'Public',
    private: 'Private',
  },
  shareDialog: {
    shareButton: 'Share',
    title: 'Share',
    description: 'Manage who can view, comment, or edit this.',
    peopleWithAccess: 'People with access',
    roleOwner: 'Owner',
    roleViewer: 'Viewer',
    roleCommenter: 'Commenter',
    roleEditor: 'Editor',
    pendingInvite: '(invited)',
    removeAccess: 'Remove {{email}}’s access',
    invitePlaceholder: 'Add people by email',
    invite: 'Invite',
    invalidEmail: 'Enter a valid email address.',
    toastInvited: 'Invited {{email}}.',
    toastInvitedEmailFailed:
      'Added {{email}}, but the invite email couldn’t be sent.',
    generalAccess: 'General access',
    scopeRestricted: 'Restricted',
    scopeOrganization: 'Your organization',
    scopeAnyoneWithLink: 'Anyone with the link',
    scopeRestrictedHint: 'Only people with access can open this.',
    scopeOrganizationHint: 'Anyone in your organization can open this.',
    scopeAnyoneWithLinkHint: 'Anyone with the link can open this.',
    copyLink: 'Copy link',
  },
  dataTable: {
    noResults: 'No results.',
  },
  geometryNotFound: {
    title: 'Geometry not found',
    goToDashboard: 'Go to dashboard',
    loadFailed: 'Something went wrong loading this geometry.',
  },
  staleWarning: {
    messageProject:
      'This project was last worked on over a week ago, so some newer features may not work with it.',
    messageStudy:
      'This study was last worked on over a week ago, so some newer features may not work with it.',
    messageGeometry:
      'This geometry was last worked on over a week ago, so some newer features may not work with it.',
    actionReupload: 'Re-upload',
    actionCreateStudy: 'Create new study',
    actionRerunDiagnosis: 'Re-run diagnosis',
    dismiss: 'Dismiss warning',
  },
  toast: {
    downloadSimulationFailed: 'Failed to download simulation output',
    downloadInputCaseFailed: 'Failed to download input case',
    visibilityUpdateFailed: 'Failed to update visibility',
    sharingUpdateFailed: 'Failed to update sharing settings',
  },
  readOnlyBanner: {
    viewerAccess: 'You have viewer access — owned by {{owner}}',
    commenterAccess: 'You have commenter access — owned by {{owner}}',
    viewerAccessNoOwner: 'You have viewer access',
    commenterAccessNoOwner: 'You have commenter access',
    anonymousAccess: 'You have view-only access',
    openInApp: 'Open in app',
    signInToOpenInApp: 'Sign in',
    preferences: 'Preferences',
    signInPrompt: 'You need access',
    signInBody: 'Sign in with an account that has access to view this.',
    signInCta: 'Sign in',
  },
} as const;
